import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { RotateCcw } from 'lucide-react';
import { adminApi } from '@/api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { formatDateTime } from '@/lib/utils';
import { DELIVERY_STATUS, ruLabel } from '@/lib/labels';

const statusBadge = (s: string) =>
  ({
    done: 'success',
    failed: 'danger',
    pending_plugin: 'default',
    sent_rcon: 'secondary',
    queued: 'secondary',
  })[s] as 'success' | 'danger' | 'default' | 'secondary' | undefined;

export function AdminDeliveries() {
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: ['admin', 'deliveries'],
    queryFn: adminApi.deliveries,
    refetchInterval: 5000,
  });

  const retry = useMutation({
    mutationFn: (id: number) => adminApi.retryDelivery(id),
    onSuccess: () => {
      toast.success('Поставлено в очередь');
      qc.invalidateQueries({ queryKey: ['admin', 'deliveries'] });
    },
  });

  return (
    <div>
      <h1 className="text-3xl font-extrabold text-eol-white mb-6">Доставки команд</h1>
      <Card>
        <CardContent className="pt-4">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-eol-desc text-left uppercase text-xs">
                  <th className="py-2 pr-4">ID</th>
                  <th className="py-2 pr-4">Заказ</th>
                  <th className="py-2 pr-4">Сервер</th>
                  <th className="py-2 pr-4">Команда</th>
                  <th className="py-2 pr-4">Статус</th>
                  <th className="py-2 pr-4">Попытки</th>
                  <th className="py-2 pr-4">Создано</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {(data as any[])?.map((d) => (
                  <tr key={d.id} className="border-t border-white/5">
                    <td className="py-2 pr-4 text-eol-desc">{d.id}</td>
                    <td className="py-2 pr-4 text-eol-desc">{d.orderId ?? '-'}</td>
                    <td className="py-2 pr-4 text-eol-white">{d.serverId}</td>
                    <td className="py-2 pr-4 text-eol-white font-mono text-xs max-w-md truncate" title={d.command}>{d.command}</td>
                    <td className="py-2 pr-4">
                      <Badge variant={statusBadge(d.status)}>{ruLabel(DELIVERY_STATUS, d.status)}</Badge>
                    </td>
                    <td className="py-2 pr-4 text-eol-desc">{d.attempts}</td>
                    <td className="py-2 pr-4 text-eol-desc">{formatDateTime(d.createdAt)}</td>
                    <td className="py-2 pr-4">
                      {(d.status === 'failed' || d.status === 'pending_plugin') && (
                        <Button size="sm" variant="outline" onClick={() => retry.mutate(d.id)} className="gap-1">
                          <RotateCcw className="h-3 w-3" /> Повторить
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
